import { showNotification } from '@mantine/notifications';
import { useListCustomProperties } from '@repo/api-codegen';
import { SettingsButton } from '@repo/common/components';
import { Button, Text } from '@repo/foundations';
import { useMemo, useState } from 'react';
import { useSuspenseFeatureAccess } from '../../../api';
import { downloadImportTemplate } from '../../../lib/models';
import { UpgradeButton } from '../UpgradeButton';
import { BASE_TARGET_COLUMNS } from './constants';
import { ImportModal } from './ImportModal';
import { getFileData } from './utils/utils';

export type ColumnMappingRecord = { csvColumns: string; secodaFields: string };

export function ImportSettings() {
	const { exportAccess } = useSuspenseFeatureAccess();
	const { data: customProperties } = useListCustomProperties({});
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [data, setData] = useState<Record<string, string>[]>([]);
	const [selectedColumns, setSelectedColumns] = useState<{
		selectedRecords: ColumnMappingRecord[];
		lastSelectedIndex: number | null;
	}>({
		selectedRecords: [],
		lastSelectedIndex: null,
	});
	const [columnMapping, setColumnMapping] = useState<Record<string, string>>(
		{}
	);
	const [showMappingModal, setShowMappingModal] = useState(false);

	const TARGET_COLUMNS = useMemo(() => {
		const customPropertyColumns =
			customProperties?.map((cp) => ({
				value: cp.name,
				label: cp.name,
			})) || [];

		return [...BASE_TARGET_COLUMNS, ...customPropertyColumns];
	}, [customProperties]);

	const handleFilesSelected = async (files: File[]): Promise<void> => {
		if (files.length === 0) {
			return;
		}
		const [file] = files;
		const fileExt = file.name.split('.').pop()?.toLowerCase();
		const reader = new FileReader();
		reader.onload = async (e) => {
			const fileData = await getFileData(e, file, fileExt);
			if (fileData === null) {
				return;
			}

			if (fileData.length === 0) {
				showNotification({
					title: 'Empty file',
					message: 'Please upload a valid CSV or Excel file with data',
					color: 'red',
				});
				return;
			}

			const initialMapping: Record<string, string> = Object.keys(
				fileData[0]
			).reduce((acc, col) => {
				const matchingField = TARGET_COLUMNS.find(
					(field) => field.value.toLowerCase() === col.toLowerCase()
				);
				return {
					...acc,
					[col]: matchingField ? matchingField.value : 'select',
				};
			}, {});
			setColumnMapping(initialMapping);

			setSelectedColumns({
				selectedRecords: Object.keys(initialMapping)
					.filter((col) => initialMapping[col] !== 'select')
					.map((col) => ({
						csvColumns: col,
						secodaFields: initialMapping[col],
					})),
				lastSelectedIndex: null,
			});

			setData(fileData);
			setSelectedFile(file);
			setShowMappingModal(true);
		};
		if (fileExt === 'csv') {
			reader.readAsText(file);
		} else {
			reader.readAsArrayBuffer(file);
		}
	};

	const openImportModal = () => {
		if (!exportAccess) {
			return;
		}

		const input = document.createElement('input');
		input.type = 'file';
		input.accept = 'text/csv, .xlsx, .xls';
		input.onchange = async (e) => {
			const file = (e.target as HTMLInputElement).files?.[0];
			if (file) {
				await handleFilesSelected([file]);
			}
		};
		input.click();
	};

	const handleDownloadTemplate = () => {
		downloadImportTemplate(customProperties || []);
	};

	return (
		<>
			<SettingsButton
				title="Import"
				description={
					<Text size="xs" color="text/secondary/default">
						Bulk import resource descriptions and custom properties{' '}
						<Button onClick={handleDownloadTemplate} variant="plain">
							Download template
						</Button>
					</Text>
				}
				buttonText="Choose file..."
				onClick={openImportModal}
			>
				{!exportAccess && <UpgradeButton feature="Import" />}
			</SettingsButton>

			<ImportModal
				fileData={data}
				selectedFile={selectedFile}
				setSelectedFile={setSelectedFile}
				selectedColumns={selectedColumns}
				setSelectedColumns={setSelectedColumns}
				columnMapping={columnMapping}
				setColumnMapping={setColumnMapping}
				showMappingModal={showMappingModal}
				setShowMappingModal={setShowMappingModal}
				targetColumns={TARGET_COLUMNS}
			/>
		</>
	);
}
